import { useState, useEffect, useMemo } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import Avatar from '../shared/Avatar';
import LoadBar from '../shared/LoadBar';

// PM Allocation: how much of each project manager's time is spoken for, month
// by month, across everything they do.
//
// A project manager is the owner of a SOW, and a delivery PM is its delivery
// owner (decision, October 2026). Their load is their whole load, from the
// shared engine every resourcing screen uses: each person's own capacity
// (contracted hours on their working days, less bank holidays, closures and
// leave) against every task they are on, spread over the days it runs.
// Draft and Issued SOWs are tentative work, weighted by win probability and
// shown beside committed work.

// ── Colour helpers ─────────────────────────────────────────────────────────────
function pctColor(pct, threshold) {
  if (pct === null) return { text: 'text-red-700', fill: 'bg-red-500' };
  if (pct === 0) return { text: 'text-gray-300', fill: 'bg-gray-200' };
  if (pct >= threshold) return { text: 'text-red-600', fill: 'bg-red-500' };
  if (pct >= threshold * 0.85) return { text: 'text-amber-600', fill: 'bg-amber-400' };
  return { text: 'text-emerald-600', fill: 'bg-emerald-400' };
}

function initials(name) {
  return (name || '').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

const r1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

/** Committed as a share of available, or null when there is work and no capacity. */
function share(days, capacity) {
  if (capacity > 0) return (days / capacity) * 100;
  return days > 0 ? null : 0;
}

function fmtPct(pct) {
  if (pct === null) return '!';
  return pct === 0 ? '·' : `${Math.round(pct)}%`;
}

// ── Delivery status pill ───────────────────────────────────────────────────────
const DS_STYLES = {
  on_track: 'bg-emerald-100 text-emerald-700',
  at_risk:  'bg-amber-100 text-amber-700',
  delayed:  'bg-orange-100 text-orange-700',
  blocked:  'bg-red-100 text-red-700',
  issued:   'bg-blue-100 text-blue-700',
  on_hold:  'bg-gray-100 text-gray-600',
  uat:      'bg-purple-100 text-purple-700',
  complete: 'bg-teal-100 text-teal-700',
};
const DS_LABEL = {
  on_track: 'On Track', at_risk: 'At Risk', delayed: 'Delayed',
  blocked: 'Blocked', issued: 'Issued', on_hold: 'On Hold', uat: 'UAT',
  complete: 'Complete',
};
function DeliveryPill({ value }) {
  const cls = DS_STYLES[value] || 'bg-gray-100 text-gray-500';
  const label = DS_LABEL[value] || value || 'No RAG';
  return (
    <span className={`text-[7px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
  );
}

// ── Clearance pill ─────────────────────────────────────────────────────────────
// Only present for holders of the People page; the server leaves it out otherwise.
function ClearancePill({ level }) {
  if (!level || level === 'none') return null;
  const labels = { sc: 'SC', dv: 'DV', bpss: 'BPSS', esc: 'eSC' };
  return (
    <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
      {labels[level?.toLowerCase()] || level.toUpperCase()}
    </span>
  );
}

function RoleTag({ role }) {
  return role === 'pm'
    ? <span className="text-[7px] font-bold px-1 py-px rounded bg-navy/10 text-navy">PM</span>
    : <span className="text-[7px] font-bold px-1 py-px rounded bg-gold/10 text-gold">Delivery PM</span>;
}

// ── Quarter navigation ─────────────────────────────────────────────────────────
function quarterLabel(q) {
  const m = q.match(/^(\d{4})Q([1-4])$/);
  if (!m) return q;
  const [, yr, qn] = m;
  const labels = { '1': 'Jan to Mar', '2': 'Apr to Jun', '3': 'Jul to Sep', '4': 'Oct to Dec' };
  return `Q${qn} · ${labels[qn]} ${yr}`;
}
function shiftQuarter(q, delta) {
  const m = q.match(/^(\d{4})Q([1-4])$/);
  if (!m) return q;
  let [, yr, qn] = m;
  yr = parseInt(yr); qn = parseInt(qn) + delta;
  if (qn < 1) { yr--; qn = 4; }
  if (qn > 4) { yr++; qn = 1; }
  return `${yr}Q${qn}`;
}
function currentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}Q${q}`;
}

const GRID = 'minmax(170px, 220px) repeat(3, minmax(0, 1fr)) minmax(64px, 90px)';

// ── Main component ─────────────────────────────────────────────────────────────
export default function PmAllocationPage() {
  const { navigate } = useApp();
  const [quarter,    setQuarter]    = useState(currentQuarter);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [threshold,  setThreshold]  = useState(80);
  const [expanded,   setExpanded]   = useState(null); // pm id or null
  const [roleFilter, setRoleFilter] = useState('all'); // all | pm | delivery_pm

  useEffect(() => {
    setLoading(true);
    setError('');
    setData(null);
    setExpanded(null);
    api.get('people/pm-allocation', { quarter })
      .then(d => setData(d))
      .catch(e => setError(e.data?.message || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [quarter]);

  const months = data?.months || [];
  const pms = useMemo(() => (data?.pms || []).filter(pm =>
    roleFilter === 'all' ? true : roleFilter === 'pm' ? pm.pm_of > 0 : pm.delivery_pm_of > 0
  ), [data, roleFilter]);

  // How many are at or over the threshold in any month, on committed work.
  const overCount = useMemo(() => pms.filter(pm =>
    pm.month_days.some((d, i) => { const s = share(d, pm.month_capacity[i]); return s === null || s >= threshold; })
  ).length, [pms, threshold]);

  return (
    <div className="fade-in">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">PM Allocation</h2>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {quarterLabel(quarter)} · SOW owners (PM) and delivery owners (delivery PM), and how much of their time is spoken for
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded border border-gray-200 overflow-hidden text-[9px]">
            {[['all', 'Everyone'], ['pm', 'PMs'], ['delivery_pm', 'Delivery PMs']].map(([k, l]) => (
              <button key={k} onClick={() => setRoleFilter(k)}
                className={`px-2 py-1 font-semibold ${roleFilter === k ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
          {/* Quarter nav */}
          <div className="flex items-center gap-1">
            <button onClick={() => setQuarter(q => shiftQuarter(q, -1))} aria-label="Previous quarter"
              className="w-7 h-7 rounded border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-navy transition-colors text-xs">‹</button>
            <span className="text-[9px] font-bold text-navy px-2 py-1 bg-white border border-gray-200 rounded min-w-[120px] text-center">
              {quarterLabel(quarter)}
            </span>
            <button onClick={() => setQuarter(q => shiftQuarter(q, +1))} aria-label="Next quarter"
              className="w-7 h-7 rounded border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-navy transition-colors text-xs">›</button>
          </div>
        </div>
      </div>

      {/* ── Threshold control ──────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 mb-3 flex items-center gap-4 flex-wrap">
        <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap min-w-[120px]">
          Capacity threshold
        </span>
        <div className="flex-1 flex items-center gap-3 min-w-[160px]">
          <span className="text-[8px] text-gray-400">40%</span>
          <input type="range" min={40} max={100} step={5} value={threshold}
            onChange={e => setThreshold(Number(e.target.value))}
            aria-label="Capacity threshold"
            className="flex-1 h-1 rounded appearance-none bg-gray-200 cursor-pointer accent-navy"
          />
          <span className="text-[8px] text-gray-400">100%</span>
        </div>
        <div className="text-lg font-extrabold text-navy tabular-nums min-w-[52px] text-right">
          {threshold}<span className="text-[10px] font-normal text-gray-400">%</span>
        </div>
        <div className="border-l border-gray-200 pl-4 text-[8px] text-gray-400 whitespace-nowrap min-w-[140px]">
          <span className="font-semibold text-gray-600">{overCount} {overCount === 1 ? 'person' : 'people'}</span> at or over threshold
        </div>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-[9px] text-amber-800">
          <strong>Could not load allocation:</strong>{' '}
          <span className="font-mono">{error}</span>
        </div>
      )}

      {loading && (
        <div className="text-center py-12 text-[9px] text-gray-400">Working out allocation…</div>
      )}

      {!loading && !error && data && (
        <>
          {/* ── Month column headers ──────────────────────────────────────── */}
          {months.length > 0 && (
            <div className="grid gap-2 mb-1" style={{ gridTemplateColumns: GRID }}>
              <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider pl-3">Person</div>
              {months.map(m => (
                <div key={m.key} className="text-center">
                  <div className="text-[7px] font-bold text-gray-500 uppercase tracking-wide">{m.label}</div>
                </div>
              ))}
              <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider text-right pr-3">Quarter</div>
            </div>
          )}

          {pms.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <div className="text-[9px] text-gray-400">Nobody owns or delivers a live or pipeline SOW in {quarterLabel(quarter)}.</div>
              <div className="text-[8px] text-gray-400 mt-1">A person appears here once they are a SOW's owner or delivery owner.</div>
            </div>
          )}

          {pms.map(pm => {
            const isOpen = expanded === pm.id;
            const totalDays = pm.month_days.reduce((a, b) => a + b, 0);
            const totalTentative = pm.month_tentative.reduce((a, b) => a + b, 0);
            const totalCap = pm.month_capacity.reduce((a, b) => a + b, 0);
            const qPct = share(totalDays, totalCap);
            const qColors = pctColor(qPct, threshold);

            return (
              <div key={pm.id} className="mb-2">
                {/* Tile row */}
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : pm.id)}
                  aria-expanded={isOpen}
                  className={`w-full text-left grid gap-0 bg-white border rounded-lg transition-all hover:shadow-md ${
                    isOpen ? 'border-navy shadow-md rounded-b-none' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ gridTemplateColumns: GRID }}
                >
                  {/* Identity */}
                  <div className="flex items-center gap-2.5 px-3 py-3 border-r border-gray-100 min-w-0">
                    <Avatar personId={pm.id} className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                      style={{ background: pm.avatar_color || 'var(--navy)' }}>
                      {initials(pm.name)}
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold text-navy truncate">{pm.name}</div>
                      <div className="flex items-center gap-1 flex-wrap mt-0.5">
                        {pm.pm_of > 0 && <span className="text-[7px] text-gray-500"><RoleTag role="pm" /> {pm.pm_of}</span>}
                        {pm.delivery_pm_of > 0 && <span className="text-[7px] text-gray-500"><RoleTag role="delivery_pm" /> {pm.delivery_pm_of}</span>}
                        <ClearancePill level={pm.security_clearance} />
                      </div>
                    </div>
                  </div>

                  {/* Month cells */}
                  {months.map((m, i) => {
                    const days = pm.month_days[i];
                    const tent = pm.month_tentative[i];
                    const cap = pm.month_capacity[i];
                    const pct = share(days, cap);
                    const { text } = pctColor(pct, threshold);
                    return (
                      <div key={m.key} className="flex flex-col items-center justify-center gap-1 px-2 py-2 border-r border-gray-100 last:border-r-0">
                        <div className={`text-sm font-bold tabular-nums ${text}`}>{fmtPct(pct)}</div>
                        <div className="w-4/5"><LoadBar committed={days} tentative={tent} capacity={cap} compact /></div>
                        <div className="text-[7px] text-gray-400">{r1(days)}d of {r1(cap)}d{tent > 0 ? <span className="text-indigo-700"> · +{r1(tent)}d bid</span> : ''}</div>
                      </div>
                    );
                  })}

                  {/* Quarter summary */}
                  <div className="flex flex-col items-end justify-center px-3 py-3">
                    <div className={`text-xs font-bold tabular-nums ${qColors.text}`}>{fmtPct(qPct)}</div>
                    <div className="text-[7px] text-gray-400">{r1(totalDays)}d of {r1(totalCap)}d</div>
                    {totalTentative > 0 && <div className="text-[7px] text-indigo-700">+{r1(totalTentative)}d bid</div>}
                  </div>
                </button>

                {/* ── Detail panel ────────────────────────────────────────── */}
                {isOpen && (
                  <div className="bg-white border border-navy border-t-0 rounded-b-lg overflow-hidden">
                    {/* Month detail row */}
                    <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                      {months.map((m, i) => {
                        const days = pm.month_days[i];
                        const cap = pm.month_capacity[i];
                        const contracted = pm.month_contracted[i];
                        const leave = pm.month_leave[i];
                        const free = Math.max(0, cap - days);
                        return (
                          <div key={m.key} className="px-5 py-3 border-r border-gray-100 last:border-r-0">
                            <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-1">{m.label}</div>
                            <div className="text-[9px] text-gray-700"><span className="font-semibold">{r1(free)}d free</span> of {r1(cap)}d available</div>
                            <div className="text-[8px] text-gray-400">
                              {r1(contracted)}d contracted{leave > 0 ? `, ${r1(leave)}d leave` : ''}{contracted - cap - leave > 0.05 ? `, ${r1(contracted - cap - leave)}d bank holidays or closures` : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Records worth checking */}
                    {((pm.notes || []).length > 0 || (pm.unplaceable || []).length > 0) && (
                      <div className="mx-5 mb-2 text-[8px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        {[...(pm.notes || []), ...(pm.unplaceable || []).map(u => `${u.sow_reference || ''} ${u.task_name}: ${u.reason}`)].join(' ')}
                      </div>
                    )}

                    {/* Project list */}
                    <div className="px-5 pb-4">
                      <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider pt-3 pb-2 border-t border-gray-100">
                        Projects this quarter
                      </div>
                      {pm.projects.length === 0 && (
                        <div className="text-[8px] text-gray-400 py-2">No projects found.</div>
                      )}
                      {pm.projects.map(proj => (
                        <div key={proj.sow_id} className="flex items-center gap-3 py-1.5 border-b border-gray-100 last:border-b-0">
                          <button type="button" onClick={() => navigate('sow-detail', proj.sow_id)}
                            className="text-[8px] text-navy font-mono w-24 flex-shrink-0 text-left hover:underline">
                            {proj.sow_reference}
                          </button>
                          <span className="flex-1 min-w-0 text-[9px] text-gray-700 truncate">
                            {proj.client_name || ''}
                          </span>
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {proj.roles.map(r => <RoleTag key={r} role={r} />)}
                            {proj.tentative && <span className="text-[7px] font-semibold px-1 py-px rounded bg-indigo-50 text-indigo-700">{proj.sow_status}</span>}
                          </span>
                          <span className="text-[8px] text-gray-500 w-32 text-right flex-shrink-0 tabular-nums">
                            {proj.month_days.map((d, i) => {
                              const t = proj.month_tentative_days[i];
                              return d > 0 ? `${r1(d)}d` : t > 0 ? `(${r1(t)}d)` : '–';
                            }).join(' / ')}
                          </span>
                          <DeliveryPill value={proj.delivery_status} />
                        </div>
                      ))}
                      <div className="text-[7px] text-gray-400 mt-1">Days per month are this person's own task work on each SOW; bid work is in brackets.</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
